import axios from "axios";
import { useAuthStore } from "@/stores/authStore";

const DEFAULT_API_TIMEOUT_MS = 30_000;

function parsePositiveTimeout(value: string | undefined, fallback: number) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

export const API_TIMEOUT_MS = parsePositiveTimeout(
  import.meta.env.VITE_API_TIMEOUT_MS,
  DEFAULT_API_TIMEOUT_MS
);

declare module "axios" {
  interface AxiosRequestConfig {
    skipAuthRedirect?: boolean;
  }
}

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "",
  timeout: API_TIMEOUT_MS,
  withCredentials: true,
});

apiClient.interceptors.request.use((config) => {
  const mswSessionId =
    typeof window !== "undefined" &&
    (window as unknown as { __MSW_SESSION_ID__?: string }).__MSW_SESSION_ID__;

  if (mswSessionId) {
    config.headers = config.headers ?? {};
    config.headers["X-MSW-Session"] = mswSessionId;
    config.params = {
      ...config.params,
      __mswSession: mswSessionId,
    };
  }

  return config;
});

// Auth interceptor - logout on 401
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only logout if not already on login page and not a logout request
      const isLogoutRequest = error.config?.url?.includes("/auth/logout");
      const shouldNotLogout = error.config?.skipAuthRedirect;

      if (!isLogoutRequest && !shouldNotLogout) {
        // Clear user state and redirect to login
        useAuthStore.getState().logout();
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;
